import { useEffect, useMemo, useState } from "react";
import { FileDown, RefreshCw, CircleDollarSign, Info, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiErrorMessage, formatRupiah } from "@/lib/api";

// -- helper: YYYYMMDD ↔ Date
function toYyyyMmDd(dateStr) {
  // dateStr = "YYYY-MM-DD" (dari input date) → "YYYYMMDD"
  if (!dateStr) return "";
  return dateStr.replace(/-/g, "");
}

function fromIsoDate(iso) {
  // "2026-07-25" → "2026-07-25" (input date native format)
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

const SPECIAL_NETTO = "__NETTO__";
const SPECIAL_GAPO = "__GAJI_POKOK__";

export function ExportCsvMandiriDialog({ open, onOpenChange, run, unitId }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pegawai, setPegawai] = useState([]);
  const [jumlahSlip, setJumlahSlip] = useState(0);
  const [totalGajiPokok, setTotalGajiPokok] = useState(0);
  const [tanggal, setTanggal] = useState("");
  const [berita, setBerita] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [selectedPegawai, setSelectedPegawai] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [search, setSearch] = useState("");
  const [searchPegawai, setSearchPegawai] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  async function fetchData() {
    if (!run) return;
    setLoading(true);
    try {
      const params = { run_id: run.id };
      if (unitId) params.unit_id = unitId;
      const [{ data: kt }, { data: settings }] = await Promise.all([
        api.get("/reports/payroll/komponen-tersedia", { params }),
        api.get("/settings/payroll-csv"),
      ]);
      const pegawaiList = kt.pegawai || [];
      setItems(kt.items || []);
      setPegawai(pegawaiList);
      setJumlahSlip(kt.jumlah_slip || 0);
      setTotalGajiPokok(kt.total_gaji_pokok || 0);
      setCfg(settings);
      // Default: semua pegawai TERCENTANG saat dialog dibuka
      setSelectedPegawai(new Set(pegawaiList.map((p) => p.user_id)));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      fetchData();
      setTanggal(fromIsoDate(run?.tanggal_gajian));
      setBerita(`GAJI ${(run?.periode_label || "").toUpperCase()}`.slice(0, 19));
      setSelected(new Set());
      setSearch("");
      setSearchPegawai("");
      setPreview(null);
    }
    // eslint-disable-next-line
  }, [open, run?.id]);

  const useNetto = selected.has(SPECIAL_NETTO);
  const includeGajiPokok = selected.has(SPECIAL_GAPO);
  const komponenNames = useMemo(
    () => Array.from(selected).filter((n) => n !== SPECIAL_NETTO && n !== SPECIAL_GAPO),
    [selected],
  );
  const adaPilihan = useNetto || includeGajiPokok || komponenNames.length > 0;
  const adaPegawaiTerpilih = selectedPegawai.size > 0;

  // Preview dihitung SERVER dengan fungsi yang sama persis dengan penulis file CSV,
  // sehingga angka di dialog = angka header P (kolom E) tanpa selisih.
  useEffect(() => {
    if (!open || !run) return;
    if (!adaPilihan || !adaPegawaiTerpilih) {
      setPreview(null);
      return;
    }
    let batal = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const body = {
          run_id: run.id,
          komponen_names: komponenNames,
          include_gaji_pokok: includeGajiPokok,
          use_netto: useNetto,
          user_ids: Array.from(selectedPegawai),
        };
        if (unitId) body.unit_id = unitId;
        const { data } = await api.post("/reports/payroll/export-mandiri-csv/preview", body);
        if (!batal) setPreview(data);
      } catch (e) {
        if (!batal) setPreview(null);
      } finally {
        if (!batal) setPreviewLoading(false);
      }
    }, 250);
    return () => {
      batal = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, [open, run?.id, unitId, useNetto, includeGajiPokok, komponenNames.join("|"), Array.from(selectedPegawai).sort().join("|")]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.nama.toLowerCase().includes(q));
  }, [items, search]);

  const filteredPegawai = useMemo(() => {
    const q = searchPegawai.trim().toLowerCase();
    if (!q) return pegawai;
    return pegawai.filter(
      (p) =>
        (p.nama || "").toLowerCase().includes(q) ||
        (p.npp || "").toLowerCase().includes(q) ||
        (p.no_rekening || "").toLowerCase().includes(q),
    );
  }, [pegawai, searchPegawai]);

  function toggle(name) {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(name)) nx.delete(name);
      else nx.add(name);
      return nx;
    });
  }

  function togglePreset(preset) {
    if (preset === "semua") {
      // Gaji Pokok + semua Pendapatan − semua Potongan (Beban Perusahaan bukan uang pegawai)
      const s = new Set([SPECIAL_GAPO]);
      for (const it of items) {
        if (it.tipe === "Pendapatan" || it.tipe === "Potongan") s.add(it.nama);
      }
      setSelected(s);
      return;
    }
    setSelected(new Set());
  }

  function togglePegawai(uid) {
    setSelectedPegawai((prev) => {
      const nx = new Set(prev);
      if (nx.has(uid)) nx.delete(uid);
      else nx.add(uid);
      return nx;
    });
  }

  function toggleAllPegawai(mode) {
    // mode: 'all' → centang semua yang match filter search; 'none' → hapus semua centang
    if (mode === "all") {
      setSelectedPegawai((prev) => {
        const nx = new Set(prev);
        for (const p of filteredPegawai) nx.add(p.user_id);
        return nx;
      });
    } else {
      setSelectedPegawai((prev) => {
        const nx = new Set(prev);
        for (const p of filteredPegawai) nx.delete(p.user_id);
        return nx;
      });
    }
  }

  async function unduh() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tanggal || "")) {
      toast.error("Isi Tanggal Transfer terlebih dahulu");
      return;
    }
    if (!berita.trim()) {
      toast.error("Isi Berita Payroll terlebih dahulu");
      return;
    }
    if (!adaPilihan) {
      toast.error("Pilih minimal satu komponen atau centang 'Gaji Bersih (Netto)'");
      return;
    }
    if (!adaPegawaiTerpilih) {
      toast.error("Pilih minimal satu pegawai yang akan diikutsertakan di CSV");
      return;
    }

    setDownloading(true);
    try {
      const body = {
        run_id: run.id,
        tanggal: toYyyyMmDd(tanggal),
        berita: berita.trim(),
        komponen_names: komponenNames,
        include_gaji_pokok: includeGajiPokok,
        use_netto: useNetto,
        user_ids: Array.from(selectedPegawai),
      };
      if (unitId) body.unit_id = unitId;
      const resp = await api.post(
        "/reports/payroll/export-mandiri-csv",
        body,
        { responseType: "blob" },
      );
      const totalBaris = resp.headers?.["x-total-baris"] || "?";
      const totalNominal = resp.headers?.["x-total-nominal"] || "0";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      // Nama file mengikuti standar Bank Mandiri: CSV_[Berita Payroll].csv
      const beritaFile = berita.trim().slice(0, 19).replace(/[\\/:*?"<>|]/g, "_");
      a.download = `CSV_${beritaFile}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(
        `CSV diunduh — ${totalBaris} baris, ${formatRupiah(Number(totalNominal) || 0)}`,
      );
      onOpenChange(false);
    } catch (e) {
      let msg = apiErrorMessage(e);
      // blob error — parse body
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  }

  const cfgMissing = cfg && (!cfg.rekening_sumber || !cfg.transfer_currency);
  const totalPegawai = pegawai.length;
  const pegawaiTerpilih = selectedPegawai.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] flex flex-col"
        data-testid="csv-mandiri-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display flex items-center gap-2">
            <FileDown size={18} className="text-teal-700" />
            CSV Transfer Bank — Format Mandiri
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Bangkitkan file CSV 44 kolom untuk diunggah ke Mandiri Cash Management. Periode:{" "}
            <b>{run?.periode_label}</b> · {jumlahSlip} pegawai · sudah otomatis mengabaikan pegawai
            tanpa No. Rekening.
          </p>
        </DialogHeader>

        {cfgMissing ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900 flex items-start gap-2">
            <Info size={13} className="mt-0.5 shrink-0" />
            <div>
              <b>Pengaturan CSV belum lengkap.</b> Isi Rekening Sumber &amp; Transfer Currency di
              menu <b>Konfigurasi Kebijakan → Pengaturan CSV Payroll</b> agar Col C &amp; Col F pada
              baris header CSV terisi.
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 shrink-0">
          <div>
            <Label className="text-xs font-bold text-slate-600">Tanggal Transfer</Label>
            <Input
              type="date"
              value={tanggal}
              onChange={(e) => setTanggal(e.target.value)}
              className="mt-1.5"
              data-testid="csv-input-tanggal"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Ditulis sebagai <span className="font-mono-clock">YYYYMMDD</span> di Col B baris header.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-slate-600">Berita Payroll (Col H)</Label>
              <span
                className={`text-[10px] font-mono-clock ${berita.length >= 19 ? "text-rose-600 font-bold" : "text-slate-400"}`}
                data-testid="csv-berita-counter"
              >
                {berita.length}/19
              </span>
            </div>
            <Input
              value={berita}
              onChange={(e) => setBerita(e.target.value.slice(0, 19))}
              placeholder="GAJI SEP 2026"
              maxLength={19}
              className="mt-1.5"
              data-testid="csv-input-berita"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Maks <b>19 karakter</b> (standar Bank Mandiri). Menjadi nama file:{" "}
              <span className="font-mono-clock">CSV_{(berita.trim() || "…")}.csv</span>
            </p>
          </div>
        </div>

        {/* Layout dua kolom: Pegawai (kiri) + Komponen (kanan). Pada layar
            sempit, keduanya tumbuh menjadi satu kolom bertumpuk. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0">
          {/* ==== PEGAWAI CHECKLIST ==== */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <Users size={13} className="text-slate-500 shrink-0" />
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider truncate">
                  Pegawai
                </div>
                <span
                  className="text-[10px] font-mono-clock text-slate-500 shrink-0"
                  data-testid="csv-pegawai-counter"
                >
                  {pegawaiTerpilih}/{totalPegawai}
                </span>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAllPegawai("all")}
                  className="h-7 text-[11px] border-teal-300 text-teal-800"
                  data-testid="csv-pegawai-select-all"
                >
                  Pilih Semua
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleAllPegawai("none")}
                  className="h-7 text-[11px]"
                  data-testid="csv-pegawai-clear"
                >
                  Kosongkan
                </Button>
              </div>
            </div>

            <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0">
              <Search size={13} className="text-slate-400" />
              <input
                className="flex-1 text-sm outline-none bg-transparent"
                placeholder="Cari nama / NPP / rekening…"
                value={searchPegawai}
                onChange={(e) => setSearchPegawai(e.target.value)}
                data-testid="csv-pegawai-search"
              />
            </div>

            <div
              className="flex-1 min-h-0 overflow-y-auto"
              data-testid="csv-pegawai-scroll"
            >
              <div className="divide-y divide-slate-100">
                {loading ? (
                  <div className="px-3 py-6 text-center text-xs text-slate-500">
                    Memuat daftar pegawai…
                  </div>
                ) : filteredPegawai.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-slate-500">
                    {pegawai.length === 0
                      ? "Belum ada slip pada periode ini."
                      : "Tidak ada pegawai cocok dengan pencarian."}
                  </div>
                ) : (
                  filteredPegawai.map((p) => {
                    const tanpaRek = !p.no_rekening;
                    return (
                      <label
                        key={p.user_id}
                        className="flex items-start gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                        data-testid={`csv-pegawai-${p.user_id}`}
                      >
                        <Checkbox
                          checked={selectedPegawai.has(p.user_id)}
                          onCheckedChange={() => togglePegawai(p.user_id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800 truncate">
                            {p.nama}
                            {p.npp ? (
                              <span className="ml-1.5 text-[10px] font-mono-clock text-slate-500">
                                ({p.npp})
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {tanpaRek ? (
                              <span className="text-rose-600 font-semibold">
                                Tanpa No. Rekening — otomatis dilewati
                              </span>
                            ) : (
                              <>
                                {p.nama_bank ? `${p.nama_bank} · ` : ""}
                                <span className="font-mono-clock">{p.no_rekening}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* ==== KOMPONEN CHECKLIST ==== */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider truncate">
                Komponen → Nominal (Col G)
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => togglePreset("semua")}
                  className="h-7 text-[11px] border-teal-300 text-teal-800"
                  data-testid="csv-preset-semua"
                >
                  Semua Kolom
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => togglePreset("clear")}
                  className="h-7 text-[11px]"
                  data-testid="csv-preset-clear"
                >
                  Kosongkan
                </Button>
              </div>
            </div>

            <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0">
              <Search size={13} className="text-slate-400" />
              <input
                className="flex-1 text-sm outline-none bg-transparent"
                placeholder="Cari nama komponen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={useNetto}
                data-testid="csv-input-search"
              />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="csv-komponen-scroll">
              <div className="divide-y divide-slate-100">
                {/* Special: Netto */}
                <label
                  className="flex items-start gap-3 px-3 py-2.5 hover:bg-teal-50/50 cursor-pointer"
                  data-testid="csv-opt-netto"
                >
                  <Checkbox
                    checked={useNetto}
                    onCheckedChange={() => toggle(SPECIAL_NETTO)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-teal-800 flex items-center gap-1.5">
                      <CircleDollarSign size={13} /> Gaji Bersih (Netto)
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Nominal = <b>netto</b> tiap pegawai (bruto − potongan − PPh21 dipotong).
                      Bila dicentang, pilihan lain diabaikan.
                    </div>
                  </div>
                </label>

                {/* Special: Gaji Pokok */}
                <label
                  className={`flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer ${
                    useNetto ? "opacity-40 pointer-events-none" : ""
                  }`}
                  data-testid="csv-opt-gaji-pokok"
                >
                  <Checkbox
                    checked={includeGajiPokok}
                    onCheckedChange={() => toggle(SPECIAL_GAPO)}
                    className="mt-0.5"
                    disabled={useNetto}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">Gaji Pokok</div>
                    <div className="text-[11px] text-slate-500">
                      Total periode: <b>{formatRupiah(totalGajiPokok)}</b> · menambahkan nilai{" "}
                      <span className="font-mono-clock">gaji_pokok</span> tiap pegawai ke Nominal.
                    </div>
                  </div>
                </label>

                {loading ? (
                  <div className="px-3 py-6 text-center text-xs text-slate-500">
                    Memuat daftar komponen…
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-slate-500">
                    Tidak ada komponen aktif di periode ini.
                  </div>
                ) : (
                  filteredItems.map((it) => {
                    const isPot = it.tipe === "Potongan";
                    const isBeban = it.tipe === "Beban Perusahaan";
                    return (
                      <label
                        key={it.nama}
                        className={`flex items-start gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer ${
                          useNetto ? "opacity-40 pointer-events-none" : ""
                        }`}
                        data-testid={`csv-opt-${it.nama}`}
                      >
                        <Checkbox
                          checked={selected.has(it.nama)}
                          onCheckedChange={() => toggle(it.nama)}
                          className="mt-0.5"
                          disabled={useNetto}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            {it.label || it.nama}
                            <span
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                isPot
                                  ? "bg-rose-50 text-rose-700 border border-rose-100"
                                  : isBeban
                                    ? "bg-indigo-50 text-indigo-700 border border-indigo-100"
                                    : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                              }`}
                            >
                              {it.tipe}
                            </span>
                            {it.system_auto ? (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-teal-600 text-white">
                                Sistem
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {it.system_auto ? (
                              <span className="text-teal-700">Hasil hitung sistem · </span>
                            ) : null}
                            Total periode: <b>{formatRupiah(it.total)}</b> · terpakai di{" "}
                            {it.count} slip
                            {isPot ? (
                              <span className="text-rose-600">
                                {" "}
                                · <b>mengurangi</b> nominal bila dicentang
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900 flex items-center justify-between gap-3 shrink-0">
          <div>
            <b>Total Nominal (Header P, Kolom E)</b>
            <div className="text-[11px] text-teal-700/80 mt-0.5" data-testid="csv-total-detail">
              {!adaPilihan ? (
                "Pilih komponen untuk melihat total yang akan ditulis ke file."
              ) : !adaPegawaiTerpilih ? (
                "Pilih minimal satu pegawai yang akan diikutsertakan."
              ) : previewLoading || !preview ? (
                "Menghitung di server…"
              ) : (
                <>
                  {preview.total_baris} baris transfer dari {preview.jumlah_slip} slip
                  {preview.dilewati_tidak_dipilih
                    ? ` · ${preview.dilewati_tidak_dipilih} tidak dipilih admin dilewati`
                    : ""}
                  {preview.dilewati_tanpa_rekening
                    ? ` · ${preview.dilewati_tanpa_rekening} tanpa No. Rekening dilewati`
                    : ""}
                  {preview.dilewati_nominal_nol
                    ? ` · ${preview.dilewati_nominal_nol} nominal ≤ 0 dilewati`
                    : ""}
                  {" · angka identik dengan isi file"}
                </>
              )}
            </div>
          </div>
          <div
            className="text-lg font-extrabold text-teal-800 font-mono-clock"
            data-testid="csv-total-preview"
          >
            {!adaPilihan || !adaPegawaiTerpilih
              ? formatRupiah(0)
              : previewLoading || !preview
                ? "…"
                : formatRupiah(preview.total_nominal || 0)}
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="csv-cancel">
            Batal
          </Button>
          <Button
            onClick={fetchData}
            variant="outline"
            className="gap-1.5"
            disabled={loading}
            data-testid="csv-refresh"
          >
            <RefreshCw size={13} /> Muat Ulang
          </Button>
          <Button
            onClick={unduh}
            disabled={downloading || loading}
            className="bg-teal-700 hover:bg-teal-800 gap-1.5"
            data-testid="csv-unduh"
          >
            <FileDown size={13} /> {downloading ? "Mengunduh…" : "Unduh CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExportCsvMandiriDialog;
